import { payzheBtpProvider } from './PayzheBtpProvider';
import { buildPayzheBtpSettingsPayload } from './buildSettingsPayload';
import { bluetoothPrinterService } from '../BluetoothPrinterService';
import {
  fetchPayzheBtpPrinters,
  fetchPayzheBtpStatus,
  getStoredPayzheBtpToken,
  postPayzheBtpConnect,
  postPayzheBtpSettings,
} from './nativeBtpClient';
import { pickDefaultPrinterId } from './NativeBtpPrinterAdapter';
import { getPayzheBtpAutoReconnect } from './bluetoothProviderSettings';
import type { PayzheBtpBootstrapPhase, PayzheBtpBootstrapResult } from './types';

export type { PayzheBtpBootstrapPhase, PayzheBtpBootstrapResult };

async function tryConnectLastPrinter(token: string): Promise<boolean> {
  if (!getPayzheBtpAutoReconnect()) return false;
  const currentStatus = await fetchPayzheBtpStatus(token);
  if (currentStatus.connected) return true;

  const printers = await fetchPayzheBtpPrinters(token);
  const targetId = await pickDefaultPrinterId(printers);
  if (!targetId) return false;

  await postPayzheBtpConnect({ printerId: targetId, transport: 'auto' }, token);
  const status = await fetchPayzheBtpStatus(token);
  return status.connected;
}

export async function bootstrapPayzheBtpBridge(options?: {
  wakeApp?: boolean | 'auto';
  maxAttempts?: number;
  connectPrinter?: boolean;
}): Promise<PayzheBtpBootstrapResult> {
  const connectPrinter = options?.connectPrinter ?? getPayzheBtpAutoReconnect();

  try {
    await payzheBtpProvider.ensureReady({ wakeApp: options?.wakeApp ?? 'auto' });

    const token = getStoredPayzheBtpToken();
    let printerConnected = payzheBtpProvider.getSnapshot().status?.connected ?? false;

    if (connectPrinter && token && !printerConnected) {
      try {
        await postPayzheBtpSettings(buildPayzheBtpSettingsPayload(), token);
        printerConnected = await tryConnectLastPrinter(token);
        await payzheBtpProvider.refreshStatus();
        printerConnected =
          printerConnected || Boolean(payzheBtpProvider.getSnapshot().status?.connected);
      } catch {
        printerConnected = Boolean(payzheBtpProvider.getSnapshot().status?.connected);
      }
    }

    if (printerConnected || payzheBtpProvider.getSnapshot().status?.connected) {
      await bluetoothPrinterService.syncNativeProviderFromBridge();
      printerConnected = true;
    }

    return {
      phase: 'ready',
      reachable: true,
      printerConnected,
    };
  } catch (error) {
    const errorKey =
      error instanceof Error && error.message.startsWith('bluetoothPrinter.')
        ? error.message
        : 'bluetoothPrinter.payzheBtp.errors.unreachable';
    const snap = payzheBtpProvider.getSnapshot();
    return {
      phase: 'failed',
      reachable: snap.state === 'ready' || snap.state === 'service_stopped',
      printerConnected: Boolean(snap.status?.connected),
      errorKey,
    };
  }
}
